import { createContext, useState, useContext, useEffect } from "react";
import { infoModeName, stylesModeName } from "./contextStrings";
import { Context as GlobalContext } from "../../../Context";

export const ApartmentViewContext = createContext(null);

const ApartmentViewContextProvider = ({ children }) => {
	const { displayableApartmentsData, projectSetup } = useContext(GlobalContext);

	const loadWithRenderTop = projectSetup?.apartments?.load_with_render_top;
	const isFloorPlanInteractive = projectSetup?.apartments?.interactive_floorplan;

	const initialMode = {
		tier: 1, //TODO: this should be based on active apartment data
		mode:  loadWithRenderTop ? stylesModeName : infoModeName,
		planView: true,
		rotatedToNorth: false,
	};
	const initialInteractions = {
		hoveredCompartmentInd: null,
		selectedCompartmentInd: null,
		isCompartmentPathHovered: false,
	};

	const initialInfoTooltipData = {
		type: "",
		hovered: false,
		position: null,
		data: [],
	};

	const [doApartmentsHaveVersions, setDoApartmentsHaveVersions] =
		useState(false);
	const [doApartmentsHaveHotSpots, setDoApartmentsHaveHotSpots] =
		useState(false);
	const [doApartmentsHaveIsometricViews, setDoApartmentsHaveIsometricViews] =
		useState(false);

	const [apartmentViewMode, setApartmentViewMode] = useState(initialMode);
    const [apartmentViewInteractions, _setApartmentViewInteractions] =
        useState(initialInteractions);
	const [infoTooltipData, setInfoTooltipData] = useState(
		initialInfoTooltipData
	);
	const [insolationAngle, setInsolationAngle] = useState(180); //180deg is noon (12:00 PM)
	const [isometricAngle, setIsometricAngle] = useState(0);
	const [isIsometryDragging, setIsIsometryDragging] = useState(false);
	const [showIsometryInstructionsPopup, setShowIsometryInstructionsPopup] =
		useState(true);
	//TODO: Add react-router-dom to project and use it to get URL params
	const [urlComboParam, setUrlComboParam] = useState(null);
	const [userSelectedArrangements, setUserSelectedArrangements] = useState([
		0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
	]); //TODO: this initial state should be fetched from URL params, or form local storage, or be based on active apartment data

	const setApartmentViewInteractions = (update) => {
		_setApartmentViewInteractions(prev => {
			// Calculate the intended next state
			const nextState = typeof update === 'function' ? update(prev) : update;

			// If the floor plan is not interactive,
			// prevent updating selectedCompartmentInd by resetting it.
			if (!isFloorPlanInteractive) {
				return {
					...prev,
					...nextState,
					selectedCompartmentInd: null,
				};
			}

			// Otherwise, apply the update as is.
			return {
				...prev,
				...nextState
			};
		});
	};

	useEffect(() => {
		if (!displayableApartmentsData) return;

		const analizeViewerModes = (data) => {
			let hotSpots = false;
			let isometricViews = false;
			let compartmentVersions = false;

			const apartmentsData = data;

			for (const apartment of apartmentsData) {
				const cameras = apartment.tiers.map((tier) => {
					tier.cameras.map((camera) => camera.name || 0);
				});
				// temporary check for cameras other than top_3D; TODO: should be changed to check for specific camera names
				const hasAIViewCamera = apartment.tiers.some(
					(tier) => tier.cameras.some((camera) => camera.name.includes("id")) //TODO: add to context stings
				);

				const hasIsometricViewCamera = apartment.tiers.some((tier) =>
					tier.cameras.some((camera) => camera.name.includes("axonometric"))
				);

				if (hasAIViewCamera) {
					hotSpots = true;
				} else if (hotSpots) {
					hotSpots = true; //not to overwrite if already true
				} else hotSpots = false;

				if (hasIsometricViewCamera) {
					isometricViews = true;
				} else if (isometricViews) {
					isometricViews = true; //not to overwrite if already true
				} else isometricViews = false;

				const versions = apartment.compartments.map(
					(compartments) => +compartments.versions || 0
				);
				if (versions.some((number) => number > 1)) {
					compartmentVersions = true;
				} else if (compartmentVersions) {
					compartmentVersions = true; //not to overwrite if already true
				} else compartmentVersions = false;
			}

			setDoApartmentsHaveVersions(compartmentVersions);
			setDoApartmentsHaveHotSpots(hotSpots);
			setDoApartmentsHaveIsometricViews(isometricViews);
		};

		analizeViewerModes(displayableApartmentsData);
	}, [displayableApartmentsData]);

	return (
		<ApartmentViewContext.Provider
			value={{
				apartmentViewMode,
				setApartmentViewMode,
				apartmentViewInteractions,
				setApartmentViewInteractions,
				infoTooltipData,
				setInfoTooltipData,
				insolationAngle,
				setInsolationAngle,
				urlComboParam,
				setUrlComboParam,
				userSelectedArrangements,
				setUserSelectedArrangements,
				doApartmentsHaveHotSpots,
				setDoApartmentsHaveHotSpots,
				doApartmentsHaveVersions,
				setDoApartmentsHaveVersions,
				doApartmentsHaveIsometricViews,
				setDoApartmentsHaveIsometricViews,
				isometricAngle,
				setIsometricAngle,
				isIsometryDragging,
				setIsIsometryDragging,
				showIsometryInstructionsPopup,
				setShowIsometryInstructionsPopup,
				isFloorPlanInteractive,
			}}
		>
			{children}
		</ApartmentViewContext.Provider>
	);
};

export default ApartmentViewContextProvider;
